import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sun, ShieldCheck, Lightning, Heart, Target, Eye, Users, Wrench } from "@phosphor-icons/react";
import SEO from "../components/SEO";
import LeadForm from "../components/LeadForm";
import api, { IMAGES } from "../lib/api";

export default function About() {
  const [content, setContent] = useState(null);
  useEffect(() => {
    api.get("/site-content").then(({ data }) => setContent(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="about-page">
      <SEO page="about" titleFallback="About Orax Solar"/>

      {/* HERO */}
      <section className="pt-32 pb-16 bg-[#F9F8F6]">
        <div className="container-custom grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="overline mb-3">ABOUT ORAX SOLAR</div>
            <h1 className="h1 mb-5">Powering Maharashtra one rooftop at a time</h1>
            <p className="text-lg text-[#2D4739] leading-relaxed">
              {content?.about_intro || "Orax Solar is Akola's leading solar installation company, serving residential, commercial, and industrial clients across Maharashtra since 2015."}
            </p>
          </div>
          <div className="md:col-span-5">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden">
              <img src={IMAGES.worker} alt="Orax solar engineer" className="w-full h-full object-cover"/>
              <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur rounded-2xl p-5 shadow-xl">
                <div className="font-display font-extrabold text-3xl text-[#135934]">10+</div>
                <div className="text-sm text-[#5C7368]">Years of solar expertise</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION VISION */}
      <section className="section-padding bg-white">
        <div className="container-custom grid md:grid-cols-2 gap-6">
          <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} className="bg-[#135934] rounded-3xl p-10 text-white">
            <Target weight="duotone" size={40} className="text-[#FDB813] mb-5"/>
            <h2 className="h2 text-white mb-4">Our Mission</h2>
            <p className="text-white/85 text-lg leading-relaxed">
              {content?.about_mission || "To make clean, affordable solar energy accessible to every home and business in Maharashtra, reducing dependence on the grid and cutting electricity costs permanently."}
            </p>
          </motion.div>
          <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:0.1}} className="bg-[#0F52BA] rounded-3xl p-10 text-white">
            <Eye weight="duotone" size={40} className="text-[#FDB813] mb-5"/>
            <h2 className="h2 text-white mb-4">Our Vision</h2>
            <p className="text-white/85 text-lg leading-relaxed">
              {content?.about_vision || "A Maharashtra powered by the sun — where every rooftop generates its own clean energy and contributes to a sustainable future."}
            </p>
          </motion.div>
        </div>
      </section>

      {/* VALUES */}
      <section className="section-padding bg-[#F9F8F6]">
        <div className="container-custom">
          <div className="max-w-xl mb-14">
            <div className="overline mb-3">WHY CUSTOMERS TRUST US</div>
            <h2 className="h2 text-[#0A1F15]">Built on integrity & engineering excellence</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: <ShieldCheck weight="duotone" size={32}/>, title: "Honest Pricing", desc: "Transparent quotes. No hidden charges. Detailed BOM shared upfront." },
              { icon: <Lightning weight="duotone" size={32}/>, title: "Quality First", desc: "Only Tier-1 panels and premium inverters. No compromise on components." },
              { icon: <Wrench weight="duotone" size={32}/>, title: "Expert Engineering", desc: "Certified engineers design every system for maximum yield." },
              { icon: <Heart weight="duotone" size={32}/>, title: "After-sales Care", desc: "Local service team. Priority response. Lifelong relationship." },
            ].map((v, i) => (
              <motion.div key={i} initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:i*0.05}} className="bg-white rounded-2xl p-7 border border-[#E5E3DB] card-lift">
                <div className="w-14 h-14 rounded-xl bg-[#135934]/10 text-[#135934] flex items-center justify-center mb-4">{v.icon}</div>
                <h3 className="h3 mb-2 text-[#0A1F15]">{v.title}</h3>
                <p className="text-[#2D4739]">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-white">
        <div className="container-custom grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <h2 className="h2 mb-4">Ready to go solar with Akola's trusted team?</h2>
            <p className="text-lg text-[#2D4739] mb-6">Get a free consultation and custom proposal within 24 hours.</p>
          </div>
          <div className="md:col-span-5">
            <div className="bg-[#F9F8F6] rounded-2xl p-7 border border-[#E5E3DB]">
              <h3 className="h3 mb-4">Get a free quote</h3>
              <LeadForm source="about"/>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
